/**
 * Customer Portal Guide
 * Features for customer self-service and tracking
 */

import {
  Users,
  Search,
  Link2,
  Mail,
  Smartphone,
  Eye,
  Shield,
  Clock,
} from 'lucide-react';
import { pageStyles, cardStyles } from '../../lib/styles';

export function CustomerPortal() {
  return (
    <div className={pageStyles.container}>
      {/* Introduction */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Portal del Cliente</span>
          </div>
        </div>
        <div className={`${cardStyles.content} prose prose-gray max-w-none`}>
          <p>
            El portal del cliente permite a tus clientes consultar el estado de
            su reparacion sin necesidad de llamar al taller. Esto mejora la
            experiencia del cliente y reduce la carga de trabajo del equipo.
          </p>
        </div>
      </div>

      {/* How Customers Access */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Como Acceden los Clientes</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 border rounded-lg">
              <div className="p-2 rounded-lg bg-blue-50">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">
                  Busqueda por Matricula + Referencia
                </h4>
                <p className="text-sm text-gray-600">
                  El cliente introduce la matricula de su vehiculo y el numero
                  de referencia de la cita. Esta es la forma mas comun de
                  acceso.
                </p>
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm font-mono">
                  URL: /track?plate=XXX&ref=XXX
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 border rounded-lg">
              <div className="p-2 rounded-lg bg-green-50">
                <Link2 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">
                  Enlace Directo de Seguimiento
                </h4>
                <p className="text-sm text-gray-600">
                  Cada cita tiene un enlace unico que puedes enviar al cliente.
                  Con este enlace accede directamente sin introducir datos.
                </p>
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm font-mono">
                  URL: /tracking/[token]
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 border rounded-lg">
              <div className="p-2 rounded-lg bg-purple-50">
                <Mail className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">
                  Magic Link (Enlace Magico)
                </h4>
                <p className="text-sm text-gray-600">
                  Clientes que ya han reservado pueden solicitar un enlace por
                  email para acceder a todas sus citas pasadas y actuales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What Customers See */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Eye className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Que Ve el Cliente</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Clock className="h-5 w-5 text-gray-500" />
              <div>
                <span className="font-medium text-gray-900">
                  Estado Actual
                </span>
                <p className="text-sm text-gray-600">
                  El estado de la reparacion con explicacion clara
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Smartphone className="h-5 w-5 text-gray-500" />
              <div>
                <span className="font-medium text-gray-900">
                  Datos del Vehiculo
                </span>
                <p className="text-sm text-gray-600">
                  Matricula, marca y modelo para confirmar que es su cita
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Clock className="h-5 w-5 text-gray-500" />
              <div>
                <span className="font-medium text-gray-900">
                  Fecha Programada
                </span>
                <p className="text-sm text-gray-600">
                  Cuando se programo la cita o cuando estara listo
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">
              Estados que ve el cliente:
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>Vehiculo pendiente de llegada</li>
              <li>Vehiculo recepcionado - En evaluacion</li>
              <li>En diagnostico</li>
              <li>Preparando presupuesto</li>
              <li>Esperando su aprobacion</li>
              <li>En reparacion</li>
              <li>Control de calidad</li>
              <li>Listo para recoger!</li>
              <li>Entregado</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Generating Tracking Links */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Generar Enlace de Seguimiento</span>
        </div>
        <div className={cardStyles.content}>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                1
              </span>
              <span className="text-gray-700">
                Ve al detalle de la cita que quieres compartir
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                2
              </span>
              <span className="text-gray-700">
                En la seccion "Enlace de Seguimiento", pulsa "Generar Enlace"
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                3
              </span>
              <span className="text-gray-700">
                Copia el enlace y envíalo al cliente por WhatsApp, email o SMS
              </span>
            </li>
          </ol>
          <div className="mt-4 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
            <strong>Nota:</strong> Cada enlace es unico y expira segun la
            configuracion del sistema. No compartas enlaces en redes sociales
            publicas.
          </div>
        </div>
      </div>

      {/* Privacy */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Privacidad y Seguridad</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>
                Las paginas de seguimiento no son indexadas por buscadores
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>
                Los enlaces de seguimiento tienen tiempo de expiracion
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>
                Se requiere matricula + referencia para evitar accesos no
                autorizados
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>
                El Magic Link solo se envia al email registrado del cliente
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default CustomerPortal;
